import React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X, FileCode } from 'lucide-react'
import type { ComponentEntry } from '../types'
import { cn } from 'components/UI/cn'
import { ErrorBoundary } from 'components/UI/ErrorBoundary'

interface ComponentCardProps {
  entry: ComponentEntry
  preview?: React.ReactNode
  /** Span the full grid row and use a taller preview area */
  wide?: boolean
}

const tierColors: Record<number, string> = {
  1: 'bg-success-bg text-success border-success-bg',
  2: 'bg-amber-50 text-amber-900 border-amber-200',
  3: 'bg-orange-50 text-orange-800 border-orange-200',
}

const tierLabels: Record<number, string> = {
  1: 'Tier 1 · Primitive',
  2: 'Tier 2 · Extended',
  3: 'Tier 3 · Composite',
}

function openInEditor(path: string): void {
  fetch(`/__open-in-editor?file=src/${path}`).catch(() => undefined)
}

function UsageFilesModal({
  entry,
  children,
}: {
  entry: ComponentEntry
  children: React.ReactNode
}) {
  const files = entry.usageFiles

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-surface rounded-lg shadow-xl w-full max-w-lg max-h-[70vh] flex flex-col focus:outline-none">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-line">
            <div>
              <Dialog.Title className="font-semibold text-fg text-sm">
                {entry.name}
              </Dialog.Title>
              <Dialog.Description className="text-xs text-fg-secondary mt-0.5">
                Used in {files.length} file{files.length !== 1 ? 's' : ''}
              </Dialog.Description>
            </div>
            <Dialog.Close className="text-fg-secondary hover:text-fg rounded p-1 hover:bg-surface-hover">
              <X size={16} />
            </Dialog.Close>
          </div>
          {/* File list */}
          <div className="overflow-y-auto flex-1 px-5 py-3">
            {files.length === 0 ? (
              <p className="text-xs text-fg-muted italic py-4 text-center">
                No usage data — run{' '}
                <code className="bg-surface-alt px-1 rounded">
                  npm run analyze-components
                </code>{' '}
                to populate.
              </p>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {files.map((file) => (
                  <li key={file}>
                    <button
                      type="button"
                      className="w-full text-left text-xs text-fg-secondary hover:text-brand hover:bg-surface-hover px-2 py-1.5 rounded font-mono"
                      onClick={() => openInEditor(file)}
                      title="Open in editor"
                    >
                      {file}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function ComponentCard({
  entry,
  preview,
  wide = false,
}: ComponentCardProps): React.ReactElement {
  return (
    <div
      className={cn(
        'border border-line rounded-lg p-4 bg-surface flex flex-col gap-3',
        wide && 'col-span-full'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-fg text-sm">{entry.name}</span>
            <span
              className={cn(
                'text-xs px-2 py-0.5 rounded-full border font-medium',
                tierColors[entry.tier]
              )}
            >
              {tierLabels[entry.tier]}
            </span>
            {entry.path.startsWith('components/FormUtilsV2/') && (
              <span className="text-xs px-2 py-0.5 rounded-full border font-medium bg-surface-alt text-fg-secondary border-line">
                FormUtilsV2
              </span>
            )}
            <UsageFilesModal entry={entry}>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-brand hover:underline px-0 py-0.5"
              >
                <FileCode size={11} />
                {entry.usageCount === 0
                  ? 'not used'
                  : entry.usageCount === 1
                    ? '1 file'
                    : `${entry.usageCount} files`}
              </button>
            </UsageFilesModal>
          </div>
          <button
            type="button"
            className="text-xxs text-fg-tertiary hover:text-brand hover:underline mt-0.5 text-left truncate block max-w-full"
            onClick={() => openInEditor(entry.path)}
            title="Open in editor (requires EDITOR env var)"
          >
            {entry.path}
          </button>
        </div>
      </div>

      {/* Preview */}
      <ErrorBoundary>
        {preview ?? (
          <span className="text-xs text-fg-muted italic">
            No preview — mock needed.{' '}
            <a
              href="https://github.com/firstbite/lonesome_2/blob/dev/docs/superpowers/specs/2026-03-25-component-library-design.md"
              className="text-brand hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              Contribution guide
            </a>
          </span>
        )}
      </ErrorBoundary>

      {/* Exported names */}
      {entry.exportedNames.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {entry.exportedNames.slice(0, 8).map((name) => (
            <code
              key={name}
              className="text-xxs bg-surface-alt text-fg-secondary px-1.5 py-0.5 rounded"
            >
              {name}
            </code>
          ))}
          {entry.exportedNames.length > 8 && (
            <span className="text-xxs text-fg-muted">
              +{entry.exportedNames.length - 8} more
            </span>
          )}
        </div>
      )}
    </div>
  )
}
